import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Chip,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import axios from 'axios';
import { useThemeMode } from '../context/ThemeModeContext';

/** Purpose: present available product plans and pricing actions. Why: prospective customers need to compare subscription options before signup. */
export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'
  const navigate = useNavigate();
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === 'dark';

  useEffect(() => {
    fetchPlans();
  }, []);

  /** Purpose: Load fetch plans for the pricing module. Why: The selected view needs current data to render or continue processing. */
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/auth/plans');
      setPlans(response.data?.plans || []);
    } catch (err) {
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const isAnnual = billingCycle === 'annual';
  /** Purpose: Perform has promotional discount for the pricing module. Why: Callers need a consistent result from this shared operation. */
  const hasPromotionalDiscount = plans.some(
    (p) => Number(p.actualPrice || 0) > Number(p.monthlyPrice || 0)
  );

  const defaultCoreFeatures = [
    'Omnichannel Web & WhatsApp Bot',
    'Voice IVR Telephony Integration',
    'Visual Drag & Drop Flow Builder',
    'Real-Time NLU & Intent Engine',
    'Multi-Tenant Data Isolation',
    'Enterprise 256-Bit SSL/TLS Encryption',
    '99.9% Production Uptime SLA',
  ];

  const faqs = [
    {
      q: 'How does the pricing and promotional discount work?',
      a: 'All plans and discounts are managed directly in Platform Control. When a promotional rate or discount is active, the discount percentage and savings are calculated automatically and locked in for your subscription.',
    },
    {
      q: 'Can I upgrade, downgrade, or cancel at any time?',
      a: 'Yes, absolutely. You can upgrade your plan or modify channel limits whenever your business grows directly through your Zelorich AI console without interruption.',
    },
    {
      q: 'Which conversational channels are supported out of the box?',
      a: 'Zelorich AI provides integrated support for embeddable Web Chat widgets, official WhatsApp Business API, and Voice Telephony IVR (SIP trunks, Twilio, Asterisk, and cloud PBX).',
    },
    {
      q: 'How does multi-tenant data isolation keep our data secure?',
      a: 'Every enterprise bot runs within an isolated tenant namespace with cryptographic API keys, role-based access control (RBAC), and 256-bit TLS/SSL encryption for all data in transit and at rest.',
    },
    {
      q: 'Do you offer custom enterprise solutions?',
      a: 'Yes. For high-volume telephony, dedicated SIP trunks, on-premise private VPC deployments, or custom fine-tuned LLM/NLU models, our solution architects provide tailored SLAs.',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: isDark ? '#020617' : '#F8FAFC',
        color: isDark ? '#F8FAFC' : '#0F172A',
        transition: 'background-color 0.3s ease',
      }}
    >
      {/* 1. TOP SAAS NAVBAR */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backdropFilter: 'blur(16px)',
          bgcolor: isDark ? 'rgba(2, 6, 23, 0.85)' : 'rgba(248, 250, 252, 0.85)',
          borderBottom: '1px solid',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(226, 232, 240, 0.9)',
          py: 1.75,
          px: { xs: 2, sm: 4, md: 6 },
        }}
      >
        <Box
          sx={{
            maxWidth: 1280,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Logo & Brand */}
          <Box
            onClick={() => navigate('/login')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 18px rgba(37, 99, 235, 0.35)',
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ color: '#ffffff', fontSize: 22 }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                    fontSize: '1.15rem',
                  }}
                >
                  Zelorich AI
                </Typography>
                <Chip
                  label="Enterprise"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    bgcolor: isDark ? 'rgba(37, 99, 235, 0.2)' : 'rgba(37, 99, 235, 0.1)',
                    color: isDark ? '#93C5FD' : '#2563EB',
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(37, 99, 235, 0.3)' : 'rgba(37, 99, 235, 0.2)',
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#94A3B8' : '#64748B',
                  fontSize: '0.72rem',
                  fontWeight: 500,
                }}
              >
                Autonomous Conversational Platform
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation Links */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 3.5,
              fontSize: '0.9rem',
              fontWeight: 600,
              color: isDark ? '#CBD5E1' : '#475569',
            }}
          >
            <Box
              component="a"
              href="#plans"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
                transition: 'color 0.2s',
                '&:hover': { color: '#3B82F6' },
              }}
            >
              Plans &amp; Pricing
            </Box>
            <Box
              component="a"
              href="#comparison"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
                transition: 'color 0.2s',
                '&:hover': { color: '#3B82F6' },
              }}
            >
              Feature Matrix
            </Box>
            <Box
              component="a"
              href="#faq"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
                transition: 'color 0.2s',
                '&:hover': { color: '#3B82F6' },
              }}
            >
              FAQ
            </Box>
            <Chip
              icon={<VerifiedUserOutlinedIcon sx={{ fontSize: '14px !important', color: '#10B981 !important' }} />}
              label="All Systems Operational"
              size="small"
              sx={{
                bgcolor: isDark ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.08)',
                color: isDark ? '#34D399' : '#059669',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                fontWeight: 700,
                fontSize: '0.72rem',
                height: 24,
              }}
            />
          </Box>

          {/* Right Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip title={`Switch to ${isDark ? 'light' : 'dark'} mode`}>
              <IconButton
                onClick={toggleMode}
                size="small"
                sx={{
                  color: isDark ? '#94A3B8' : '#64748B',
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#FFFFFF',
                  },
                }}
              >
                {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate('/login')}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                borderColor: isDark ? 'rgba(255,255,255,0.12)' : '#E2E8F0',
                color: isDark ? '#CBD5E1' : '#475569',
                '&:hover': {
                  borderColor: isDark ? '#3B82F6' : '#2563EB',
                },
              }}
            >
              Back to Login
            </Button>

            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={() => navigate('/login')}
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                px: 2,
                py: 0.8,
                background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
              }}
            >
              Sign In
            </Button>
          </Box>
        </Box>
      </Box>

      {/* 2. HERO SECTION */}
      <Box
        sx={{
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 8 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 350, md: 700 },
            height: { xs: 350, md: 450 },
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.15) 0%, rgba(124, 58, 237, 0.08) 50%, transparent 70%)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '15px !important', color: '#60A5FA !important' }} />}
            label="NEXT-GEN CONVERSATIONAL INFRASTRUCTURE"
            size="small"
            sx={{
              fontWeight: 800,
              fontSize: '0.72rem',
              letterSpacing: '0.08em',
              mb: 2.5,
              px: 1.5,
              py: 0.5,
              bgcolor: isDark ? 'rgba(37, 99, 235, 0.16)' : 'rgba(37, 99, 235, 0.08)',
              color: isDark ? '#93C5FD' : '#1D4ED8',
              border: '1px solid',
              borderColor: isDark ? 'rgba(37, 99, 235, 0.3)' : 'rgba(37, 99, 235, 0.2)',
            }}
          />

          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 900,
              letterSpacing: '-0.035em',
              fontSize: { xs: '2.2rem', sm: '3rem', md: '3.6rem' },
              lineHeight: 1.12,
              mb: 2.5,
              maxWidth: 900,
              mx: 'auto',
            }}
          >
            Predictable, transparent pricing for modern{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              AI Agents
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: isDark ? '#94A3B8' : '#64748B',
              fontSize: { xs: '1rem', md: '1.18rem' },
              lineHeight: 1.65,
              maxWidth: 720,
              mx: 'auto',
              mb: 4,
            }}
          >
            Deploy intelligent autonomous bots across Web, WhatsApp Business, and Voice IVR telephony.
            All plans feature transparent usage quotas, multi-tenant data isolation, and visual flow orchestration.
          </Typography>

          {/* Promotional Banner (Rendered when discount is set in Platform Control) */}
          {hasPromotionalDiscount && (
            <Box
              sx={{
                maxWidth: 700,
                mx: 'auto',
                p: 1.75,
                borderRadius: '16px',
                bgcolor: isDark ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.08)',
                border: '1px solid',
                borderColor: isDark ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                mb: 4,
              }}
            >
              <LocalOfferOutlinedIcon sx={{ color: '#10B981', fontSize: 22 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#34D399' : '#047857' }}>
                Special Promotional Discount Active! Save with our limited-time offer rates.
              </Typography>
              <Chip
                label="Limited Time Offer"
                size="small"
                sx={{
                  bgcolor: '#10B981',
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            </Box>
          )}

          {/* Billing Switcher (Monthly vs Annual with 20% discount) */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              p: 0.5,
              borderRadius: '14px',
              bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#E2E8F0',
              border: '1px solid',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.06)',
            }}
          >
            <Button
              size="small"
              onClick={() => setBillingCycle('monthly')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.86rem',
                borderRadius: '10px',
                px: 2.5,
                py: 0.75,
                color: billingCycle === 'monthly' ? (isDark ? '#FFFFFF' : '#0F172A') : isDark ? '#94A3B8' : '#64748B',
                bgcolor: billingCycle === 'monthly' ? (isDark ? '#1E293B' : '#FFFFFF') : 'transparent',
                boxShadow: billingCycle === 'monthly' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              Monthly Billing
            </Button>

            <Button
              size="small"
              onClick={() => setBillingCycle('annual')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.86rem',
                borderRadius: '10px',
                px: 2.5,
                py: 0.75,
                color: billingCycle === 'annual' ? (isDark ? '#FFFFFF' : '#0F172A') : isDark ? '#94A3B8' : '#64748B',
                bgcolor: billingCycle === 'annual' ? (isDark ? '#1E293B' : '#FFFFFF') : 'transparent',
                boxShadow: billingCycle === 'annual' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              Annual Billing
              <Chip
                label="Save 20% Extra"
                size="small"
                sx={{
                  bgcolor: '#10B981',
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: '0.68rem',
                  height: 20,
                  letterSpacing: '0.02em',
                }}
              />
            </Button>
          </Box>
        </Container>
      </Box>

      {/* 3. DYNAMIC PRICING CARDS FROM PLATFORM CONTROL */}
      <Box id="plans" sx={{ py: 6, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="lg">
          {loading ? (
            <Box
              sx={{
                py: 12,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <CircularProgress size={42} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Fetching live plans from Platform Control...
              </Typography>
            </Box>
          ) : plans.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: '24px',
                bgcolor: isDark ? '#0F172A' : '#FFFFFF',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                No active plans configured
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Plans configured in Platform Control will appear here automatically.
              </Typography>
            </Paper>
          ) : (
            <Grid
              container
              spacing={3.5}
              alignItems="stretch"
              justifyContent="center"
            >
              {plans.map((plan, index) => {
                const actual = Number(plan.actualPrice || 0);
                const discounted = Number(plan.monthlyPrice || 0);
                const hasDiscount = actual > discounted;
                const savings = hasDiscount ? actual - discounted : 0;
                const discountPercent = hasDiscount
                  ? Math.round(((actual - discounted) / actual) * 100)
                  : 0;

                // Annual billing calculation (20% off)
                const effectiveMonthly = isAnnual ? Math.round(discounted * 0.8) : discounted;
                const effectiveActual = isAnnual && actual > 0 ? Math.round(actual * 0.8) : actual;

                const planFeatures =
                  Array.isArray(plan.features) && plan.features.length > 0
                    ? plan.features
                    : defaultCoreFeatures;

                const isFeatured = index === 0;

                return (
                  <Grid
                    item
                    xs={12}
                    sm={plans.length === 1 ? 10 : 6}
                    md={plans.length === 1 ? 6 : plans.length === 2 ? 6 : 4}
                    key={plan.code || plan._id}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        p: { xs: 3, sm: 4.5 },
                        borderRadius: '24px',
                        border: '2px solid',
                        borderColor: isFeatured
                          ? '#3B82F6'
                          : isDark
                            ? 'rgba(255, 255, 255, 0.08)'
                            : '#E2E8F0',
                        bgcolor: isDark ? '#0F172A' : '#FFFFFF',
                        boxShadow: isFeatured
                          ? isDark
                            ? '0 24px 48px -12px rgba(37, 99, 235, 0.45)'
                            : '0 24px 48px -12px rgba(37, 99, 235, 0.22)'
                          : isDark
                            ? '0 12px 30px -10px rgba(0,0,0,0.5)'
                            : '0 12px 30px -10px rgba(15, 23, 42, 0.06)',
                        position: 'relative',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      {/* Popular / Recommended Tag */}
                      {isFeatured && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -14,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Chip
                            icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '14px !important', color: '#ffffff !important' }} />}
                            label="RECOMMENDED PLAN"
                            size="small"
                            sx={{
                              fontWeight: 900,
                              fontSize: '0.72rem',
                              letterSpacing: '0.06em',
                              background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                              color: '#ffffff',
                              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
                              px: 1,
                            }}
                          />
                        </Box>
                      )}

                      {/* Header */}
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            fontSize: '1.45rem',
                            mb: 0.75,
                          }}
                        >
                          {plan.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDark ? '#94A3B8' : '#64748B',
                            fontSize: '0.88rem',
                            minHeight: 36,
                            lineHeight: 1.45,
                          }}
                        >
                          {plan.description ||
                            'Omnichannel conversational AI bots across Web, WhatsApp Business & Voice IVR'}
                        </Typography>
                      </Box>

                      {/* Price Section */}
                      <Box
                        sx={{
                          my: 2,
                          p: 2.5,
                          borderRadius: '18px',
                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#F1F5F9',
                          border: '1px solid',
                          borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                        }}
                      >
                        {/* Strikethrough Actual Price (Only if actualPrice > monthlyPrice in Platform Control) */}
                        {hasDiscount && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: 'line-through',
                                color: isDark ? '#64748B' : '#94A3B8',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                              }}
                            >
                              {plan.currency} {effectiveActual.toLocaleString()}
                            </Typography>
                            <Chip
                              label={`${discountPercent}% OFF`}
                              size="small"
                              sx={{
                                bgcolor: isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)',
                                color: isDark ? '#34D399' : '#059669',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                fontWeight: 800,
                                fontSize: '0.72rem',
                                height: 22,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? '#34D399' : '#047857',
                                fontWeight: 700,
                                fontSize: '0.78rem',
                              }}
                            >
                              Save {plan.currency} {savings.toLocaleString()}
                            </Typography>
                          </Box>
                        )}

                        {/* Offer Price */}
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: 900,
                              letterSpacing: '-0.035em',
                              color: isFeatured ? '#3B82F6' : 'inherit',
                              fontSize: { xs: '2rem', sm: '2.5rem' },
                            }}
                          >
                            {plan.currency} {effectiveMonthly.toLocaleString()}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDark ? '#94A3B8' : '#64748B',
                              fontWeight: 600,
                            }}
                          >
                            {isAnnual ? '/ month (billed yearly)' : '/ month'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Quotas Breakdown directly from plan.limits */}
                      {plan.limits && (
                        <Box sx={{ my: 2 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                              color: isDark ? '#64748B' : '#94A3B8',
                              display: 'block',
                              mb: 1.5,
                            }}
                          >
                            Monthly Quotas Included
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.86rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: isDark ? '#94A3B8' : '#64748B' }}>
                                <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 17 }} />
                                <span>Messages</span>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {plan.limits.messagesPerMonth === 0 ? 'Unlimited' : `${Number(plan.limits.messagesPerMonth).toLocaleString()}/mo`}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.86rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: isDark ? '#94A3B8' : '#64748B' }}>
                                <PhoneInTalkRoundedIcon sx={{ fontSize: 17 }} />
                                <span>Voice Telephony</span>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {plan.limits.voiceMinutesPerMonth === 0 ? 'Unlimited' : `${Number(plan.limits.voiceMinutesPerMonth).toLocaleString()} mins`}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.86rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: isDark ? '#94A3B8' : '#64748B' }}>
                                <SpeedRoundedIcon sx={{ fontSize: 17 }} />
                                <span>NLU Inferences</span>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {plan.limits.nluInferencesPerMonth === 0 ? 'Unlimited' : `${Number(plan.limits.nluInferencesPerMonth).toLocaleString()}/mo`}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.86rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: isDark ? '#94A3B8' : '#64748B' }}>
                                <BoltOutlinedIcon sx={{ fontSize: 17 }} />
                                <span>API / Webhooks</span>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {plan.limits.apiExecutionsPerMonth === 0 ? 'Unlimited' : `${Number(plan.limits.apiExecutionsPerMonth).toLocaleString()}/mo`}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.86rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: isDark ? '#94A3B8' : '#64748B' }}>
                                <StorageOutlinedIcon sx={{ fontSize: 17 }} />
                                <span>Knowledge Storage</span>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {plan.limits.storageMb === 0 ? 'Unlimited' : `${Number(plan.limits.storageMb).toLocaleString()} MB`}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}

                      <Divider sx={{ my: 2.5, borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0' }} />

                      {/* Features Included List */}
                      <Box sx={{ mb: 3.5, flexGrow: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: isDark ? '#64748B' : '#94A3B8',
                            display: 'block',
                            mb: 1.5,
                          }}
                        >
                          Capabilities &amp; Features
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                          {planFeatures.map((feat, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                              <CheckCircleRoundedIcon
                                sx={{
                                  fontSize: 18,
                                  color: '#10B981',
                                  mt: 0.2,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.88rem',
                                  color: isDark ? '#CBD5E1' : '#334155',
                                  lineHeight: 1.4,
                                }}
                              >
                                {feat}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      {/* CTA Button */}
                      <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        onClick={() => navigate('/login')}
                        sx={{
                          mt: 'auto',
                          py: 1.4,
                          borderRadius: '14px',
                          fontWeight: 800,
                          textTransform: 'none',
                          fontSize: '0.98rem',
                          letterSpacing: '-0.01em',
                          background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                          boxShadow: '0 8px 24px rgba(37, 99, 235, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                          },
                        }}
                      >
                        {hasDiscount ? `Get Started with ${plan.name}` : `Choose ${plan.name} Plan`}
                      </Button>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Enterprise Custom SLA Banner */}
          <Paper
            elevation={0}
            sx={{
              mt: 6,
              p: { xs: 3, sm: 4 },
              borderRadius: '20px',
              border: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0',
              bgcolor: isDark ? '#0F172A' : '#FFFFFF',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
                <ShieldOutlinedIcon sx={{ color: '#3B82F6', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                  Need a Custom Enterprise SLA or Dedicated Telephony Trunks?
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 700, fontSize: '0.88rem' }}>
                We support private VPC on-premise deployments, custom SIP trunk PBX connections, fine-tuned domain LLMs, and 24/7 dedicated solution architect support.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="medium"
              onClick={() => {
                window.location.href = 'mailto:sales@zelorich.com?subject=Enterprise%20Chatbot%20Inquiry';
              }}
              sx={{
                flexShrink: 0,
                borderRadius: '12px',
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                py: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#CBD5E1',
                color: isDark ? '#FFFFFF' : '#0F172A',
                '&:hover': {
                  borderColor: '#3B82F6',
                },
              }}
            >
              Contact Enterprise Sales
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* 4. VALUE PROPOSITION HIGHLIGHTS */}
      <Box
        sx={{
          py: 8,
          px: { xs: 2, sm: 3 },
          bgcolor: isDark ? 'rgba(255,255,255,0.015)' : '#F1F5F9',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#E2E8F0',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1 }}>
              Engineered for Mission-Critical Conversational Scale
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Everything required to orchestrate enterprise AI bots under one roof.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '18px',
                  bgcolor: isDark ? '#0F172A' : '#FFFFFF',
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0',
                  height: '100%',
                }}
              >
                <HubRoundedIcon sx={{ fontSize: 32, color: '#3B82F6', mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>
                  Omnichannel Core
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                  Unify Web Chat, WhatsApp Business Cloud, and Voice IVR telephony in a single automated logic tree.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '18px',
                  bgcolor: isDark ? '#0F172A' : '#FFFFFF',
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0',
                  height: '100%',
                }}
              >
                <SpeedRoundedIcon sx={{ fontSize: 32, color: '#10B981', mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>
                  Real-Time NLU &amp; AI
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                  Sub-120ms natural language intent extraction, entity classification, and generative fallback capabilities.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '18px',
                  bgcolor: isDark ? '#0F172A' : '#FFFFFF',
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0',
                  height: '100%',
                }}
              >
                <ShieldOutlinedIcon sx={{ fontSize: 32, color: '#8B5CF6', mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>
                  Multi-Tenant Security
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                  Isolated tenant databases, granular RBAC permissions, and 256-bit encryption for all records.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '18px',
                  bgcolor: isDark ? '#0F172A' : '#FFFFFF',
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0',
                  height: '100%',
                }}
              >
                <HeadsetMicOutlinedIcon sx={{ fontSize: 32, color: '#F59E0B', mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>
                  Live Agent Workspace
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                  Seamless human-in-the-loop takeover, supervisor monitoring, and automated department routing.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 5. DYNAMIC COMPARISON MATRIX FROM PLATFORM CONTROL */}
      {plans.length > 0 && (
        <Box id="comparison" sx={{ py: 10, px: { xs: 2, sm: 3 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1 }}>
                Plan Capabilities &amp; Quotas Matrix
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Detailed side-by-side comparison of active subscription plans.
              </Typography>
            </Box>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: '20px',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0',
                bgcolor: isDark ? '#0F172A' : '#FFFFFF',
                overflow: 'hidden',
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 800, fontSize: '0.95rem', py: 2.25 }}>Features &amp; Quotas</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center" sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#3B82F6' }}>
                        {p.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Monthly Price Row */}
                  <TableRow sx={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 700, py: 1.75 }}>Monthly Rate</TableCell>
                    {plans.map((p) => {
                      const actual = Number(p.actualPrice || 0);
                      const discounted = Number(p.monthlyPrice || 0);
                      return (
                        <TableCell key={p.code} align="center">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {actual > discounted && (
                              <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                {p.currency} {actual.toLocaleString()}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#3B82F6' }}>
                              {p.currency} {discounted.toLocaleString()}/mo
                            </Typography>
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {/* Messages Quota Row */}
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)', borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>Messages Quota</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {p.limits?.messagesPerMonth === 0 ? 'Unlimited' : `${Number(p.limits?.messagesPerMonth || 0).toLocaleString()}/mo`}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Voice Telephony Minutes Row */}
                  <TableRow sx={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>Voice Telephony Minutes</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {p.limits?.voiceMinutesPerMonth === 0 ? 'Unlimited' : `${Number(p.limits?.voiceMinutesPerMonth || 0).toLocaleString()} mins`}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* NLU Inferences Row */}
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)', borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>NLU Intent Inferences</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {p.limits?.nluInferencesPerMonth === 0 ? 'Unlimited' : `${Number(p.limits?.nluInferencesPerMonth || 0).toLocaleString()}/mo`}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* API Executions Row */}
                  <TableRow sx={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>API / Webhook Executions</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {p.limits?.apiExecutionsPerMonth === 0 ? 'Unlimited' : `${Number(p.limits?.apiExecutionsPerMonth || 0).toLocaleString()}/mo`}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Knowledge Base Storage Row */}
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)', borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>Knowledge Base Storage</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {p.limits?.storageMb === 0 ? 'Unlimited' : `${Number(p.limits?.storageMb || 0).toLocaleString()} MB`}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Web Chat Widget */}
                  <TableRow sx={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>Web Chat Widget</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 20 }} />
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* WhatsApp Business API */}
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)', borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>WhatsApp Business API</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 20 }} />
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Voice IVR Telephony PBX */}
                  <TableRow sx={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>Voice IVR PBX Telephony</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 20 }} />
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Multi-Tenant Data Isolation */}
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)', borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>Multi-Tenant Data Isolation</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 20 }} />
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* 256-Bit SSL/TLS Encryption */}
                  <TableRow sx={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>256-Bit SSL/TLS Security</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 20 }} />
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Production Uptime SLA */}
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)', borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#E2E8F0' }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.75 }}>Production SLA</TableCell>
                    {plans.map((p) => (
                      <TableCell key={p.code} align="center">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#10B981', fontSize: '0.85rem' }}>
                          99.9% Uptime SLA
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Container>
        </Box>
      )}

      {/* 6. FAQ SECTION */}
      <Box
        id="faq"
        sx={{
          py: 8,
          px: { xs: 2, sm: 3 },
          bgcolor: isDark ? 'rgba(255,255,255,0.015)' : '#F1F5F9',
          borderTop: '1px solid',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#E2E8F0',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <HelpOutlineRoundedIcon sx={{ color: '#3B82F6', fontSize: 22 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#3B82F6', letterSpacing: '0.04em' }}>
                FREQUENTLY ASKED QUESTIONS
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1 }}>
              Everything you need to know
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Have questions about billing, quotas, or deployment? We are here to help.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  bgcolor: isDark ? '#0F172A' : '#FFFFFF',
                  borderRadius: '16px !important',
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon sx={{ color: isDark ? '#94A3B8' : '#64748B' }} />}
                  sx={{ py: 1 }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {faq.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2.5 }}>
                  <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B', lineHeight: 1.6 }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* 7. BOTTOM CALL TO ACTION */}
      <Box sx={{ py: 10, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 6 },
              borderRadius: '28px',
              textAlign: 'center',
              background: isDark
                ? 'linear-gradient(135deg, #0F172A 0%, #1E1B4B 100%)'
                : 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
              color: '#FFFFFF',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 24px 60px -15px rgba(0, 0, 0, 0.4)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2.5,
                boxShadow: '0 8px 24px rgba(37, 99, 235, 0.4)',
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ color: '#ffffff', fontSize: 28 }} />
            </Box>

            <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5, fontSize: { xs: '1.8rem', sm: '2.5rem' } }}>
              Ready to automate your conversations?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.75)',
                fontSize: '1.05rem',
                maxWidth: 600,
                mx: 'auto',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Sign in to your Zelorich console to create multi-channel bots, build visual workflows, and connect WhatsApp &amp; Voice Telephony in minutes.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={() => navigate('/login')}
                sx={{
                  fontWeight: 800,
                  textTransform: 'none',
                  borderRadius: '14px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  boxShadow: '0 8px 24px rgba(37, 99, 235, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                  },
                }}
              >
                Sign In to Dashboard
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  window.location.href = 'mailto:sales@zelorich.com?subject=Enterprise%20Chatbot%20Inquiry';
                }}
                sx={{
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '14px',
                  px: 3.5,
                  py: 1.5,
                  fontSize: '1rem',
                  color: '#FFFFFF',
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                Talk to Sales
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* 8. FOOTER */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: { xs: 2, sm: 4 },
          borderTop: '1px solid',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#E2E8F0',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldOutlinedIcon sx={{ fontSize: 18, color: '#3B82F6' }} />
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600 }}>
                Enterprise Grade Security &bull; 256-Bit SSL/TLS &bull; Multi-Tenant Isolated
              </Typography>
            </Box>

            <Typography variant="caption" sx={{ color: isDark ? '#64748B' : '#94A3B8' }}>
              &copy; {new Date().getFullYear()} Zelorich AI. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
